import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import { getCookie, setCookie, deleteCookie } from 'cookies-next'
import { BACKEND_URL, SAME_ORIGIN_API_PROXY, resolveClientBackendBaseURL, switchToApiProxy } from './resolve-backend-base-url'
import { trackEvent } from '@/lib/analytics'

// A dead session may fail many parallel requests; report it once per page load.
let sessionExpiredReported = false

// 90 dias em segundos para expiração dos cookies de autenticação
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60 // 7776000 segundos

/**
 * Headers HTTP só aceitam ISO-8859-1 (Latin-1). Valores com code points fora
 * desse range (emoji, aspas tipográficas, CJK) fazem `setRequestHeader` lançar
 * "String contains non ISO-8859-1 code point" e derrubam a request inteira —
 * o que o fluxo de auth interpreta como falha de login. Removemos esses
 * caracteres: id/email válidos são ASCII (ficam intactos) e o nome é usado só
 * para exibição no backend.
 */
const toLatin1Header = (value: unknown): string =>
  Array.from(String(value ?? ''))
    .filter((c) => {
      const code = c.codePointAt(0) ?? 0
      return code >= 0x20 && code <= 0xff
    })
    .join('')
    .trim()

/**
 * HttpClient - Cliente HTTP base com interceptors e refresh automático de token
 * Todos os services devem usar esta instância
 */
export class HttpClient {
  protected api: AxiosInstance
  private refreshPromise: Promise<any> | null = null

  constructor() {
    this.api = axios.create({
      baseURL: resolveClientBackendBaseURL(),
      timeout: 30000, // 30 segundos - previne requisições pendentes indefinidamente
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor - adiciona token automaticamente
    this.api.interceptors.request.use(
      (config) => {
        if (typeof window !== 'undefined') {
          config.baseURL = resolveClientBackendBaseURL()
        }

        // Para FormData, deixa o browser/axios definir o Content-Type com boundary correto.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
          const headers = config.headers as any
          if (headers?.delete) {
            headers.delete('Content-Type')
          } else if (headers) {
            delete headers['Content-Type']
            delete headers['content-type']
          }
        }

        const token = getCookie('access_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        const userCookie = getCookie('user')
        if (userCookie) {
          try {
            const storedUser = typeof userCookie === 'string'
              ? JSON.parse(userCookie)
              : userCookie
            if (storedUser?.id) {
              const v = toLatin1Header(storedUser.id)
              if (v) config.headers['x-user-id'] = v
            }
            if (storedUser?.email) {
              const v = toLatin1Header(storedUser.email)
              if (v) config.headers['x-user-email'] = v
            }
            if (storedUser?.name) {
              const v = toLatin1Header(storedUser.name)
              if (v) config.headers['x-user-name'] = v
            }
          } catch {
            // ignore invalid persisted user
          }
        }

        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor - refresh automático
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any

        // No response at all on a direct call = the browser blocked it (CORS for a
        // white-label domain the backend doesn't know yet) or the network failed:
        // retry once through the same-origin proxy and keep using it in this tab.
        if (
          typeof window !== 'undefined' &&
          error.code === AxiosError.ERR_NETWORK &&
          originalRequest &&
          !originalRequest._retryViaProxy &&
          resolveClientBackendBaseURL() !== SAME_ORIGIN_API_PROXY
        ) {
          originalRequest._retryViaProxy = true
          switchToApiProxy()
          return this.api.request(originalRequest)
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          // Don't intercept login attempts - let them handle their own errors
          if (originalRequest.url?.includes('/auth/login')) {
            return Promise.reject(error)
          }

          const isPublicCatalogGet =
            originalRequest.method?.toLowerCase() === 'get' &&
            (originalRequest.url?.includes('/categories/') ||
              originalRequest.url?.includes('/products/showcase'))

          if (isPublicCatalogGet && !originalRequest._retryWithoutAuth) {
            originalRequest._retryWithoutAuth = true
            delete originalRequest.headers.Authorization
            delete originalRequest.headers['x-user-id']
            delete originalRequest.headers['x-user-email']
            delete originalRequest.headers['x-user-name']
            this.clearAuth()
            return this.api.request(originalRequest)
          }

          // refreshPromise is assigned synchronously (no await before it):
          // concurrent 401s from parallel requests must all await this same
          // promise — any async gap here lets a sibling request see null and
          // reject without retry (the first-load 401 on the Zoom page).
          if (!this.refreshPromise) {
            const refreshToken = getCookie('refresh_token')

            if (!refreshToken) {
              //this.clearAuth() // desativado
              return Promise.reject(error)
            }

            this.refreshPromise = this.refreshToken(refreshToken as string)
              .catch((refreshError) => {
                if (!sessionExpiredReported) {
                  sessionExpiredReported = true
                  trackEvent('session_expired')
                }
                this.clearAuth()
                throw refreshError
              })
              .finally(() => {
                this.refreshPromise = null
              })
          }

          try {
            const tokens = await this.refreshPromise
            if (tokens) {
              originalRequest._retry = true
              originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
              return this.api.request(originalRequest)
            }
          } catch {
            return Promise.reject(error)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  private async refreshToken(refreshToken: string) {
    try {
      const response = await axios.post(`${resolveClientBackendBaseURL()}/auth/refresh`, {
        refresh_token: refreshToken,
      })

      const { access_token, refresh_token } = response.data

      setCookie('access_token', access_token, { maxAge: COOKIE_MAX_AGE })
      setCookie('refresh_token', refresh_token, { maxAge: COOKIE_MAX_AGE })

      return response.data
    } catch (error) {
      throw error
    }
  }

  protected clearAuth() {
    deleteCookie('access_token')
    deleteCookie('refresh_token')
    deleteCookie('user')
    // The persisted auth store must fall with the cookies, or pages keep
    // trusting a dead session and render raw 401s instead of asking for login.
    // Dynamic import because auth-store imports the api layer (cycle).
    // setState on purpose (not the store's clearAuth): the public-catalog
    // retry path also lands here, and it must not drop the visitor_mode cookie.
    import('@/lib/stores/auth-store')
      .then(({ useAuthStore }) =>
        useAuthStore.setState({ user: null, isAuthenticated: false }),
      )
      .catch(() => undefined)
  }

  // Métodos genéricos para uso direto se necessário
  get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.api.get<T>(url, config)
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.api.post<T>(url, data, config)
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.api.put<T>(url, data, config)
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.api.patch<T>(url, data, config)
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.api.delete<T>(url, config)
  }
}

// Instância singleton do HttpClient
export const httpClient = new HttpClient()

/**
 * The same auth headers the interceptor adds, for calls that cannot go through
 * axios (e.g. a `fetch` that reads a Server-Sent Events stream).
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const token = getCookie('access_token')
  if (token) headers.Authorization = `Bearer ${token}`
  const userCookie = getCookie('user')
  if (userCookie) {
    try {
      const storedUser = typeof userCookie === 'string' ? JSON.parse(userCookie) : userCookie
      const id = toLatin1Header(storedUser?.id)
      const email = toLatin1Header(storedUser?.email)
      if (id) headers['x-user-id'] = id
      if (email) headers['x-user-email'] = email
    } catch {
      // ignore invalid persisted user
    }
  }
  return headers
}
